import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { useNavigate } from 'react-router-dom';
import { Layout, ErrorNotice } from '../components/Layout';
import { useCapture } from '../context/capture';
import { cropAndCompress, normalizeHeic, sha256Hex } from '../lib/image-editor';
import { api } from '../lib/api';
import { Turnstile } from '../components/Turnstile';

export function ImageEditorPage() {
  const navigate = useNavigate();
  const { file, sourceMode, templateType, activityPreset } = useCapture();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const previewBlobRef = useRef<Blob | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [uploadProgress, setUploadProgress] = useState('');
  const receiveTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const isPdf = Boolean(
    file &&
    /\.pdf$/i.test(file.name) &&
    (file.type === 'application/pdf' ||
      file.type === '' ||
      file.type === 'application/octet-stream'),
  );
  // Keep the initial request small. Large PDFs are still rendered page-by-page
  // from the original File below, but are not bundled with the preview image.
  // This avoids a browser/edge connection reset before an appointment is created.
  const includeSourcePdf = Boolean(file && isPdf && file.size <= 8 * 1024 * 1024);

  useEffect(() => {
    if (!file) {
      navigate('/capture', { replace: true });
      return;
    }
    let active = true;
    let url = '';
    previewBlobRef.current = null;
    const preview = isPdf
      ? import('../lib/pdf-preview').then(({ renderPdfFirstPage }) => renderPdfFirstPage(file))
      : normalizeHeic(file);
    void preview
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        previewBlobRef.current = blob;
        setImageUrl(url);
      })
      .catch(() =>
        setError(
          isPdf
            ? 'ไม่สามารถอ่านหน้าแรกของ PDF ได้ กรุณาตรวจสอบว่าไฟล์ไม่เสียหายหรือมีรหัสผ่าน'
            : 'ไม่สามารถแปลงไฟล์ HEIC ได้ กรุณาลองเลือกรูปอื่น',
        ),
      );
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file, isPdf, navigate]);

  const confirm = async () => {
    const pdfPreview = previewBlobRef.current;
    if (!imageUrl || (sourceMode === 'camera' && !area) || (isPdf && !pdfPreview)) return;
    setBusy(true);
    setError('');
    try {
      let blob: Blob;
      if (sourceMode === 'file') {
        if (isPdf) {
          if (!pdfPreview) return;
          blob = pdfPreview;
        } else {
          blob = await normalizeHeic(file!);
        }
      } else {
        blob = await cropAndCompress(imageUrl, area!, rotation);
      }
      if (blob.size > 10 * 1024 * 1024) throw new Error('รูปหลังบีบอัดยังใหญ่เกิน 10 MB');
      setUploadProgress('กำลังสร้างใบนัด…');
      const created = await api<{ data: { id: string } }>('/api/appointments', {
        method: 'POST',
        body: JSON.stringify({ timezone: 'Asia/Bangkok', templateType }),
      });
      if (activityPreset === 'golf') sessionStorage.setItem(`nadmai:activity-preset:${created.data.id}`, 'golf');
      const form = new FormData();
      const imageExtension = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/png' ? 'png' : 'webp';
      form.set('image', new File([blob], `appointment.${imageExtension}`, { type: blob.type }));
      if (file && isPdf && includeSourcePdf) form.set('sourceFile', file, file.name);
      form.set('clientSha256', await sha256Hex(blob));
      setUploadProgress('กำลังอัปโหลดภาพสำหรับอ่านข้อมูล…');
      await api(`/api/appointments/${created.data.id}/image`, { method: 'POST', body: form });
      if (file && isPdf) {
        const { renderPdfPages } = await import('../lib/pdf-preview');
        await renderPdfPages(
          file,
          async ({ pageNumber, totalPages, sourcePages, blob: pageBlob }) => {
            setUploadProgress(`กำลังเตรียม PDF หน้า ${pageNumber}/${totalPages}`);
            const pageForm = new FormData();
            const extension = pageBlob.type === 'image/png' ? 'png' : pageBlob.type === 'image/jpeg' ? 'jpg' : 'webp';
            pageForm.set(
              'page',
              new File([pageBlob], `page-${pageNumber}.${extension}`, { type: pageBlob.type }),
            );
            pageForm.set('totalPages', String(totalPages));
            pageForm.set('sourcePages', String(sourcePages));
            await api(`/api/appointments/${created.data.id}/pages/${pageNumber}`, {
              method: 'POST',
              body: pageForm,
            });
          },
          templateType === 'meeting' ? 20 : 1,
        );
      }
      setUploadProgress('กำลังส่งเอกสารเข้าคิวอ่านข้อมูล…');
      await api(`/api/appointments/${created.data.id}/extract`, {
        method: 'POST',
        body: JSON.stringify({ turnstileToken }),
      });
      navigate(`/appointments/${created.data.id}/processing`, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'อัปโหลดไฟล์เอกสารไม่สำเร็จ กรุณาลองใหม่',
      );
    } finally {
      setTurnstileToken('');
      setTurnstileReset((value) => value + 1);
      setUploadProgress('');
      setBusy(false);
    }
  };

  return (
    <Layout title={sourceMode === 'camera' ? 'ครอบตัดและหมุนภาพ' : 'ตรวจสอบไฟล์ต้นฉบับ'}>
      <section className="mx-auto max-w-2xl space-y-4">
        <div className="relative h-[55vh] min-h-80 overflow-hidden rounded-2xl bg-slate-900">
          {imageUrl && sourceMode === 'camera' && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={3 / 4}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropAreaChange={(_, pixels) => setArea(pixels)}
              onCropComplete={(_, pixels) => setArea(pixels)}
            />
          )}
          {imageUrl && sourceMode === 'file' && (
            <img
              src={imageUrl}
              alt="ตัวอย่างไฟล์ต้นฉบับ"
              className="h-full w-full object-contain"
            />
          )}
        </div>
        {sourceMode === 'camera' && <div className="card space-y-4">
          <label className="label" htmlFor="zoom">ขยายภาพและครอบตัด</label>
          <input
            id="zoom"
            className="w-full accent-teal-700"
            type="range"
            min="1"
            max="3"
            step="0.05"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setRotation((value) => value - 90)}
            >
              ↶ หมุนซ้าย
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setRotation((value) => value + 90)}
            >
              ↷ หมุนขวา
            </button>
          </div>
        </div>}
        {error && <ErrorNotice message={error} />}
        {isPdf && !includeSourcePdf && (
          <p className="text-xs text-slate-500">
            PDF ขนาดใหญ่จะถูกอ่านจากทุกหน้าของไฟล์เต็ม โดยแยกส่งทีละหน้าเพื่อให้อัปโหลดเสถียรขึ้น
          </p>
        )}
        <Turnstile onToken={receiveTurnstileToken} resetKey={turnstileReset} />
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/capture')}
            disabled={busy}
          >
            ถ่ายใหม่
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void confirm()}
            disabled={busy || (sourceMode === 'camera' && !area) || !turnstileToken}
          >
            {busy ? uploadProgress || 'กำลังอัปโหลด…' : sourceMode === 'camera' ? 'ใช้ภาพนี้' : 'ใช้ไฟล์เต็มนี้'}
          </button>
        </div>
      </section>
    </Layout>
  );
}
